import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import IrpAppService from '../../../../application/platform/scp/irp/irp-app-service';
import { ACTION_TYPES, useIrpSearchDispatch } from './irp-search-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { IIrpPageRequest } from '../../../../model/scp/irp/request/irp-paged-request';
import { parseNumberInput } from '../../../../utils/format';
import { parseSort } from '../../shared/scp-utils';
import { IrpSortField } from '../../../../model/scp/irp/irp-sort-field';
import { SortDirection } from '../../../../model/scp/base/sort-direction';
import { IIrpPagedResponse } from '../../../../model/scp/irp/response/irp-paged-response';

const DEFAULT_SORT = { sortBy: IrpSortField.UpdatedDate, sortDirection: SortDirection.Descending };

type SearchParameters = IIrpPageRequest & { page: number; pageSize: number };

const EMPTY_FILTERS: Partial<SearchParameters> = { ...DEFAULT_SORT, term: undefined, clientId: undefined, modalityId: undefined, year: undefined };

export const useIrpSearchController = () => {
    const navigate = useNavigate();
    const { showError, showSuccess, confirmDeleteWithJustification, withLoading } = useFeedback();
    const dispatch = useIrpSearchDispatch();
    const { updateParameters, handleSearch, handleClearFilters, handlePaginationChange } = useSearchActions<SearchParameters>(dispatch, ACTION_TYPES, EMPTY_FILTERS);

    useEffect(() => {
        BaseAppService.listModality()
            .then((modality) => dispatch({ type: ACTION_TYPES.setModality, payload: modality }))
            .catch((error: any) => showError('Não foi possível carregar os filtros de pesquisa', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        switch (name) {
            case 'input-term':
                return updateParameters({ term: value || undefined });
            case 'select-modality':
                return updateParameters({ modalityId: parseNumberInput(value) });
            case 'select-client':
                return updateParameters({ clientId: parseNumberInput(value) });
            case 'input-year':
                return updateParameters({ year: parseNumberInput(value) });
            case 'select-sort':
                return updateParameters(parseSort(value) ?? DEFAULT_SORT);
        }
    }

    function navigateToCreate() {
        navigate('/intencao-registro-preco/publicar');
    }

    function navigateToEdit(row: IIrpPagedResponse) {
        navigate(`/intencao-registro-preco/publicar/${row.id}`);
    }

    function handleDelete(row: IIrpPagedResponse, onDeleted: () => void) {
        confirmDeleteWithJustification(
            `Tem certeza que deseja excluir a intenção de registro de preço "${row.numberIrp}"?`,
            'Informe a justificativa para excluir a intenção de registro de preço',
            async (justification) => {
                try {
                    await withLoading(() => IrpAppService.deleteIrp(justification, row.id));
                    showSuccess('Intenção de registro de preço excluída com sucesso!');
                    onDeleted();
                } catch (error: any) {
                    showError('Não foi possível excluir a intenção de registro de preço', error);
                }
            }
        );
    }

    return {
        handleOnChange,
        handleSearch,
        handleClearFilters,
        handlePaginationChange,
        navigateToCreate,
        navigateToEdit,
        handleDelete,
    };
};
